if (!window.ReusableCarousel) {
    window.ReusableCarousel = class ReusableCarousel {
        constructor(options = null) {
            this.instances = new Map();
            this.resizeRaf = 0;
            this.lazyObserver = null;
            this.constructorOptions = this.normalizeConstructorOptions(options);

            this.init = this.init.bind(this);
            this.initSpecific = this.initSpecific.bind(this);
            this.handleResize = this.handleResize.bind(this);
            this.handleSectionLoad = this.handleSectionLoad.bind(this);
            this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

            if (this.constructorOptions) {
                this.initSpecific(this.constructorOptions);
            } else {
                this.init();
            }
        }

        normalizeConstructorOptions(options) {
            if (!options || typeof options !== 'object') return null;

            const hasConfig =
                options.selector ||
                options.element ||
                options.container ||
                options.track ||
                options.slide ||
                options.prev ||
                options.next ||
                options.dotsContainer ||
                options.progressContainer ||
                options.dotClass ||
                options.activeClass ||
                typeof options.autoplay === 'boolean' ||
                typeof options.showDots === 'boolean' ||
                typeof options.showProgress === 'boolean' ||
                typeof options.enableDrag === 'boolean' ||
                typeof options.enableWheel === 'boolean' ||
                typeof options.loop === 'boolean' ||
                typeof options.scrollCount !== 'undefined' ||
                typeof options.autoplayMode !== 'undefined' ||
                typeof options.autoplaySpeed !== 'undefined' ||
                typeof options.delay !== 'undefined';

            return hasConfig ? { ...options } : null;
        }

        debounce(callback, wait) {
            let timeout;

            return (...args) => {
                clearTimeout(timeout);

                timeout = setTimeout(() => {
                    callback.apply(this, args);
                }, wait);
            };
        }

        init(root = document) {
            const carousels = root.querySelectorAll(
                '[data-carousel="true"]:not([data-carousel-initialized])'
            );

            for (let i = 0; i < carousels.length; i++) {
                const carousel = carousels[i];

                carousel.dataset.carouselInitialized = 'true';

                if (this.shouldLazySetup(root, carousel)) {
                    this.observeLazyCarousel(carousel);
                } else {
                    this.setupCarousel(carousel);
                }
            }

            if (!window.__reusableCarouselGlobalEvents) {
                window.__reusableCarouselGlobalEvents = true;

                window.addEventListener(
                    'resize',
                    this.handleResize,
                    { passive: true }
                );

                document.addEventListener(
                    'shopify:section:load',
                    this.handleSectionLoad
                );

                document.addEventListener(
                    'visibilitychange',
                    this.handleVisibilityChange
                );
            }
        }

        initSpecific(options) {
            const root = options.root instanceof Element ? options.root : document;
            const selector = options.selector || options.element;

            if (selector instanceof Element) {
                selector.dataset.carouselInitialized = 'true';
                this.setupCarousel(selector, options);
                return;
            }

            if (!selector || typeof selector !== 'string') return;

            const carousels = root.querySelectorAll(selector);
            if (!carousels.length) return;

            carousels.forEach((carousel) => {
                carousel.dataset.carouselInitialized = 'true';
                this.setupCarousel(carousel, options);
            });
        }

        shouldLazySetup(root, carousel) {
            return (
                root === document &&
                'IntersectionObserver' in window &&
                !(window.Shopify && Shopify.designMode) &&
                !this.isElementNearViewport(carousel, 600)
            );
        }

        isElementNearViewport(element, margin = 0) {
            const rect = element.getBoundingClientRect();

            return (
                rect.bottom >= -margin &&
                rect.top <= window.innerHeight + margin
            );
        }

        observeLazyCarousel(carousel) {
            if (!this.lazyObserver) {
                this.lazyObserver = new IntersectionObserver(
                    (entries) => {
                        for (let i = 0; i < entries.length; i++) {
                            if (!entries[i].isIntersecting) continue;

                            this.lazyObserver.unobserve(entries[i].target);
                            this.setupCarousel(entries[i].target);
                        }
                    },
                    { rootMargin: '600px 0px' }
                );
            }

            this.lazyObserver.observe(carousel);
        }

        handleSectionLoad(event) {
            this.init(event.target);
        }

        handleVisibilityChange() {
            const hidden = document.hidden;

            this.instances.forEach((state) => {
                if (hidden) {
                    this.stopAutoplay(state);
                } else if (state.config.autoplay) {
                    this.startAutoplay(state);
                }
            });
        }

        setupCarousel(carousel, options = null) {
            if (this.instances.has(carousel)) return;

            const config = this.getCarouselConfig(carousel, options);

            const container =
                carousel.querySelector(config.containerSelector) || carousel;

            const track = carousel.querySelector(config.trackSelector);

            const slides = carousel.querySelectorAll(config.slideSelector);

            if (!container || !track || !slides.length) return;

            const section = carousel.closest('section');

            const scopedQuery = (selector) => {
                if (!selector) return null;

                return (
                    carousel.querySelector(selector) ||
                    carousel.parentElement?.querySelector(selector) ||
                    section?.querySelector(selector) ||
                    document.querySelector(selector)
                );
            };

            const prevBtn = scopedQuery(config.prevSelector);
            const nextBtn = scopedQuery(config.nextSelector);
            const dotsContainer = config.showDots
                ? scopedQuery(config.dotsSelector)
                : null;
            const progressContainer = config.showProgress
                ? scopedQuery(config.progressSelector)
                : null;

            const state = {
                carousel,
                config,
                container,
                track,
                slides,
                slidesLength: slides.length,
                prevBtn,
                nextBtn,
                dotsContainer,
                progressContainer,
                progressBar:
                    progressContainer?.querySelector('.carousel-progress__bar'),
                currentIndex: 0,
                activeDotIndex: -1,
                slideWidth: 1,
                slideGap: 0,
                slideDistance: 1,
                visibleSlides: 1,
                maxIndex: 0,
                maxScroll: 0,
                scrollCount: 1,
                cachedDots: [],
                interval: 0,
                rafId: 0,
                autoplayLastTime: 0,
                uiRaf: 0,
                uiUpdate: null,
                autoplayVisible: true,
                autoplayPaused: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
                pointerInside: false,
                focusInside: false,
                autoplayObserver: null,
                nativeSnap: config.autoplayMode !== 'continuous',
                continuousLoopWidth: 0,
                lastKnownIndex: -1,
                autoPlayX: 0,
                wheelRaf: 0,
                wheelDelta: 0,
                lastProgress: -1
            };

            this.enableNativeSnap(state);

            if (this.isNativeOnlyCarousel(state)) {
                return;
            }

            this.instances.set(carousel, state);

            this.calculateLayout(state);
            this.renderDots(state);
            this.attachEvents(state);
            this.bindPauseControl(state);
            this.updateUI(state);

            // Re-measure when the carousel or its cards change size: a hidden
            // tab becoming visible, or late CSS/images shrinking the cards
            // after init (which would leave on-screen cards inert).
            const remeasure = () => {
                if (state.remeasureRaf) return;
                state.remeasureRaf = requestAnimationFrame(() => {
                    state.remeasureRaf = 0;
                    this.calculateLayout(state);
                    this.renderDots(state);
                    this.updateUI(state);
                });
            };

            if ('ResizeObserver' in window) {
                const observer = new ResizeObserver(remeasure);
                observer.observe(state.container);
                if (state.slides[0]) observer.observe(state.slides[0]);
            }

            if (document.readyState !== 'complete') {
                window.addEventListener('load', remeasure, { once: true });
            }

            if (config.autoplay) {
                this.observeAutoplayVisibility(state);
                this.startAutoplay(state);
            }
        }

        isNativeOnlyCarousel(state) {
            return (
                state.nativeSnap &&
                !state.prevBtn &&
                !state.nextBtn &&
                !state.config.autoplay &&
                !state.config.showDots &&
                !state.config.showProgress &&
                !state.config.enableWheel
            );
        }

        enableNativeSnap(state) {
            if (!state.nativeSnap) return;

            state.container.style.scrollSnapType = 'x mandatory';
            state.container.style.scrollBehavior = 'smooth';

            for (let i = 0; i < state.slidesLength; i++) {
                state.slides[i].style.scrollSnapAlign = 'start';
            }
        }

        getCarouselConfig(carousel, options = null) {
            const d = carousel.dataset;
            const o = options || {};

            const parseScroll = (v) => {
                return v === 'visible'
                    ? 'visible'
                    : parseInt(v, 10) || 1;
            };

            const resolveScrollCount = (value, fallback) => {
                if (typeof value === 'number') {
                    return {
                        desktop: value,
                        tablet: value,
                        mobile: value
                    };
                }

                if (value && typeof value === 'object') {
                    return {
                        desktop: value.desktop ?? value.tablet ?? value.mobile ?? fallback.desktop,
                        tablet: value.tablet ?? value.mobile ?? value.desktop ?? fallback.tablet,
                        mobile: value.mobile ?? value.tablet ?? value.desktop ?? fallback.mobile
                    };
                }

                return fallback;
            };

            const scrollCount = resolveScrollCount(o.scrollCount, {
                desktop: parseScroll(d.carouselScrollDesktop),
                tablet: parseScroll(d.carouselScrollTablet),
                mobile: parseScroll(d.carouselScrollMobile)
            });

            return {
                containerSelector:
                    o.container || d.carouselContainer || '.carousel-container',

                trackSelector:
                    o.track || d.carouselTrack || '.carousel-track',

                slideSelector:
                    o.slide || d.carouselSlide || '.carousel-slide',

                prevSelector:
                    o.prev || d.carouselPrev || '.carousel-prev',

                nextSelector:
                    o.next || d.carouselNext || '.carousel-next',

                dotsSelector:
                    o.dotsContainer || d.carouselDotsContainer || '.carousel-dots',

                progressSelector:
                    o.progressContainer || d.carouselProgressContainer || '.carousel-progress',

                dotClass:
                    o.dotClass || d.carouselDotClass || 'carousel-dot',

                activeClass:
                    o.activeClass || d.carouselActiveClass || 'active',

                autoplay:
                    typeof o.autoplay === 'boolean' ? o.autoplay : d.carouselAutoplay === 'true',

                autoplayMode:
                    o.autoplayMode || d.carouselAutoplayMode || 'slide',

                autoplaySpeed:
                    typeof o.autoplaySpeed === 'number'
                        ? o.autoplaySpeed
                        : parseFloat(d.carouselAutoplaySpeed) || 0.4,

                delay:
                    typeof o.delay === 'number'
                        ? o.delay
                        : parseInt(d.carouselDelay, 10) || 3000,

                showDots:
                    typeof o.showDots === 'boolean' ? o.showDots : d.carouselShowDots === 'true',

                showProgress:
                    typeof o.showProgress === 'boolean' ? o.showProgress : d.carouselShowProgress === 'true',

                enableDrag:
                    typeof o.enableDrag === 'boolean' ? o.enableDrag : d.carouselEnableDrag !== 'false',

                enableWheel:
                    typeof o.enableWheel === 'boolean' ? o.enableWheel : d.carouselEnableWheel === 'true',

                loop:
                    typeof o.loop === 'boolean' ? o.loop : d.carouselLoop !== 'false',

                scrollCount
            };
        }

        calculateLayout(state) {
            const slideWidth = state.slides[0].offsetWidth || 1;

            let gap = 0;

            if (state.slidesLength > 1) {
                gap =
                    state.slides[1].offsetLeft -
                    state.slides[0].offsetLeft -
                    slideWidth;
            }

            if (gap <= 0) {
                const styles = window.getComputedStyle(state.track);

                gap =
                    parseFloat(styles.columnGap) ||
                    parseFloat(styles.gap) ||
                    0;
            }

            const distance = slideWidth + gap;

            state.slideWidth = slideWidth;
            state.slideGap = gap;
            state.slideDistance = distance;

            const visible = Math.max(
                1,
                Math.round(state.container.clientWidth / distance)
            );

            state.visibleSlides = visible;

            state.scrollCount = this.getResponsiveScrollCount(
                state.config.scrollCount,
                visible
            );

            state.maxIndex = Math.max(
                0,
                state.slidesLength - visible
            );

            state.maxScroll =
                state.container.scrollWidth -
                state.container.clientWidth;

            if (state.config.autoplayMode === 'continuous') {
                state.continuousLoopWidth =
                    distance * state.slidesLength;
            }
        }

        getResponsiveScrollCount(scrollCount, visibleSlides) {
            const width = window.innerWidth;

            const value =
                width < 768
                    ? scrollCount.mobile
                    : width < 1041
                        ? scrollCount.tablet
                        : scrollCount.desktop;

            return value === 'visible'
                ? visibleSlides
                : value || 1;
        }

        renderDots(state) {
            const { dotsContainer, config } = state;

            if (!config.showDots || !dotsContainer) return;

            const totalPages = Math.max(
                1,
                Math.ceil(
                    (state.slidesLength - state.visibleSlides) /
                    state.scrollCount
                ) + 1
            );

            if (state.cachedDots.length === totalPages) return;

            dotsContainer.innerHTML = '';

            const fragment = document.createDocumentFragment();

            const dots = new Array(totalPages);

            for (let i = 0; i < totalPages; i++) {
                const dot = document.createElement('button');

                dot.type = 'button';
                dot.className = config.dotClass;
                dot.setAttribute('aria-label', `Go to slide ${i + 1}`);

                dot.onclick = () => {
                    this.scrollToIndex(
                        state,
                        i * state.scrollCount
                    );
                };

                dots[i] = dot;

                fragment.appendChild(dot);
            }

            state.cachedDots = dots;

            dotsContainer.appendChild(fragment);
        }

        attachEvents(state) {
            const {
                container,
                prevBtn,
                nextBtn,
                config,
                carousel
            } = state;

            if (config.showDots || config.showProgress || !config.loop) {
                state.uiUpdate =
                    state.uiUpdate ||
                    (() => {
                        this.scheduleUIUpdate(state);
                    });

                container.addEventListener(
                    'scroll',
                    state.uiUpdate,
                    { passive: true }
                );
            }

            if (prevBtn) {
                prevBtn.onclick = () => {
                    this.prev(state);
                };
            }

            if (nextBtn) {
                nextBtn.onclick = () => {
                    this.next(state);
                };
            }

            if (config.enableDrag) {
                this.attachDrag(state);
            }

            if (config.enableWheel) {
                this.attachWheel(state);
            }

        }

        attachWheel(state) {
            const container = state.container;

            container.addEventListener(
                'wheel',
                (event) => {
                    if (
                        Math.abs(event.deltaY) >
                        Math.abs(event.deltaX)
                    ) {
                        state.wheelDelta += event.deltaY;

                        if (state.wheelRaf) return;

                        state.wheelRaf = requestAnimationFrame(() => {
                            container.scrollLeft += state.wheelDelta;
                            state.wheelDelta = 0;
                            state.wheelRaf = 0;
                        });
                    }
                },
                { passive: true }
            );
        }

        attachDrag(state) {
            if (!state.config.autoplay && state.nativeSnap) return;

            const container = state.container;

            container.addEventListener(
                'pointerdown',
                () => {
                    state.pointerInside = true;
                    this.syncAutoplayPause(state);
                },
                { passive: true }
            );

            container.addEventListener(
                'pointerup',
                () => {
                    if (state.nativeSnap) {
                        this.snapToNearest(state);
                    }

                    this.syncAutoplayPause(state);
                },
                { passive: true }
            );

            container.addEventListener(
                'pointercancel',
                () => {
                    this.syncAutoplayPause(state);
                },
                { passive: true }
            );
        }

        getNearestIndex(state) {
            return Math.max(
                0,
                Math.min(
                    state.maxIndex,
                    Math.round(
                        state.container.scrollLeft /
                        state.slideDistance
                    )
                )
            );
        }

        snapToNearest(state) {
            const nearest = this.getNearestIndex(state);
            const left = nearest * state.slideDistance;
            const isAlreadySnapped =
                Math.abs(state.container.scrollLeft - left) < 2;

            if (
                nearest === state.lastKnownIndex &&
                isAlreadySnapped
            ) {
                return;
            }

            state.lastKnownIndex = nearest;

            this.scrollToIndex(state, nearest);
        }

        scrollToIndex(
            state,
            index,
            behavior = 'smooth'
        ) {
            const nextIndex = Math.max(
                0,
                Math.min(index, state.maxIndex)
            );

            const left = nextIndex * state.slideDistance;

            if (
                nextIndex === state.currentIndex &&
                Math.abs(
                    state.container.scrollLeft - left
                ) < 2
            ) {
                return;
            }

            state.currentIndex = nextIndex;

            state.container.scrollTo({
                left,
                behavior
            });

            this.updateUI(state);
        }

        next(state) {
            const nextIndex =
                this.getNearestIndex(state) +
                state.scrollCount;

            this.scrollToIndex(
                state,
                state.config.loop
                    ? nextIndex > state.maxIndex
                        ? 0
                        : nextIndex
                    : Math.min(nextIndex, state.maxIndex)
            );
        }

        prev(state) {
            const prevIndex =
                this.getNearestIndex(state) -
                state.scrollCount;

            this.scrollToIndex(
                state,
                state.config.loop
                    ? prevIndex < 0
                        ? state.maxIndex
                        : prevIndex
                    : Math.max(prevIndex, 0)
            );
        }

        updateUI(state) {
            const current = this.getNearestIndex(state);

            this.updateDots(state, current);
            this.updateSlideExposure(state, current);
            this.updateArrows(state, current);
            this.updateProgress(state);
        }

        scheduleUIUpdate(state) {
            if (state.uiRaf) return;

            state.uiRaf = requestAnimationFrame(() => {
                state.uiRaf = 0;
                this.updateUI(state);
            });
        }

        updateDots(state, current) {
            const dots = state.cachedDots;

            if (!dots.length) return;

            const currentPage = Math.round(
                current / state.scrollCount
            );

            if (currentPage === state.activeDotIndex) return;

            state.activeDotIndex = currentPage;

            for (let i = 0; i < dots.length; i++) {
                const isCurrent = i === currentPage;
                dots[i].classList.toggle(
                    state.config.activeClass,
                    isCurrent
                );
                if (isCurrent) dots[i].setAttribute('aria-current', 'true');
                else dots[i].removeAttribute('aria-current');
            }
        }

        updateSlideExposure(state, current) {
            const slides = state.slides;
            if (!slides || !slides.length) return;

            // Use real geometry, not visibleSlides: a carousel measured while
            // hidden (inactive tab) gets visibleSlides = 1 and would leave
            // only one usable card. Partly visible cards stay interactive
            // because inert also blocks mouse clicks.
            const box = state.container.getBoundingClientRect();

            if (box.width === 0) {
                slides.forEach((slide) => {
                    slide.inert = false;
                    slide.removeAttribute('aria-hidden');
                });
                return;
            }

            slides.forEach((slide) => {
                const rect = slide.getBoundingClientRect();
                const shown =
                    Math.min(rect.right, box.right) -
                    Math.max(rect.left, box.left);
                const isActive = shown > 1;
                slide.inert = !isActive;
                slide.setAttribute('aria-hidden', String(!isActive));
            });
        }

        syncAutoplayPause(state) {
            if (!state.config.autoplay) return;

            state.autoplayPaused = !!(state.pointerInside || state.focusInside);
            if (state.autoplayPaused) this.stopAutoplay(state);
            else this.startAutoplay(state);
        }

        bindPauseControl(state) {
            const root = state.carousel;

            root.addEventListener('pointerenter', () => {
                state.pointerInside = true;
                this.syncAutoplayPause(state);
            });
            root.addEventListener('pointerleave', () => {
                state.pointerInside = false;
                this.syncAutoplayPause(state);
            });
            root.addEventListener('focusin', () => {
                state.focusInside = true;
                this.syncAutoplayPause(state);
            });
            root.addEventListener('focusout', (event) => {
                if (!root.contains(event.relatedTarget)) {
                    state.focusInside = false;
                    this.syncAutoplayPause(state);
                }
            });
        }

        updateArrows(state, current) {
            if (state.config.loop) return;

            if (state.prevBtn) {
                state.prevBtn.disabled = current <= 0;
            }

            if (state.nextBtn) {
                state.nextBtn.disabled =
                    current >= state.maxIndex;
            }
        }

        updateProgress(state) {
            if (
                !state.config.showProgress ||
                !state.progressBar
            ) {
                return;
            }

            const total =
                state.config.autoplayMode === 'continuous'
                    ? state.continuousLoopWidth
                    : state.maxScroll;

            const progress =
                total > 0
                    ? Math.min(
                        1,
                        Math.max(
                            0,
                            state.container.scrollLeft / total
                        )
                    )
                    : 1;

            const roundedProgress = Math.round(progress * 1000) / 1000;

            if (roundedProgress === state.lastProgress) return;

            state.lastProgress = roundedProgress;

            state.progressBar.style.transform =
                `scaleX(${roundedProgress})`;
        }

        startAutoplay(state) {
            this.stopAutoplay(state);

            if (
                state.autoplayPaused ||
                document.hidden ||
                !state.autoplayVisible
            ) {
                return;
            }

            if (
                state.config.autoplayMode === 'continuous'
            ) {
                const container = state.container;

                const speed =
                    state.config.autoplaySpeed;

                const loopWidth =
                    state.continuousLoopWidth;

                if (loopWidth <= 0) return;

                state.autoPlayX =
                    container.scrollLeft;
                state.autoplayLastTime = 0;

                const tick = (timestamp) => {
                    if (
                        state.autoplayPaused ||
                        document.hidden ||
                        !state.autoplayVisible
                    ) {
                        state.rafId = 0;
                        state.autoplayLastTime = 0;
                        return;
                    }

                    if (!state.autoplayLastTime) {
                        state.autoplayLastTime = timestamp;
                    }

                    const delta = Math.min(
                        50,
                        timestamp - state.autoplayLastTime
                    );

                    state.autoplayLastTime = timestamp;
                    state.autoPlayX += speed * 60 * (delta / 1000);

                    if (state.autoPlayX >= loopWidth) {
                        state.autoPlayX = 0;
                    }

                    container.scrollLeft = state.autoPlayX;
                    state.rafId = requestAnimationFrame(tick);
                };

                state.rafId = requestAnimationFrame(tick);

                return;
            }

            state.interval = setInterval(() => {
                this.next(state);
            }, state.config.delay);
        }

        stopAutoplay(state) {
            if (state.interval) {
                clearInterval(state.interval);
                state.interval = 0;
            }

            if (state.rafId) {
                cancelAnimationFrame(state.rafId);
                state.rafId = 0;
                state.autoplayLastTime = 0;
            }

        }

        observeAutoplayVisibility(state) {
            if (!('IntersectionObserver' in window)) return;

            state.autoplayObserver = new IntersectionObserver(
                (entries) => {
                    state.autoplayVisible = entries[0].isIntersecting;

                    if (state.autoplayVisible) {
                        this.startAutoplay(state);
                    } else {
                        this.stopAutoplay(state);
                    }
                },
                { rootMargin: '100px 0px' }
            );

            state.autoplayObserver.observe(state.carousel);
        }

        handleResize() {
            if (this.resizeRaf) {
                cancelAnimationFrame(this.resizeRaf);
            }

            this.resizeRaf = requestAnimationFrame(() => {
                this.instances.forEach((state) => {
                    this.calculateLayout(state);
                    this.renderDots(state);
                    this.updateUI(state);
                });
            });
        }
    };

    document.addEventListener(
        'DOMContentLoaded',
        () => {
            window.reusableCarousel =
                new window.ReusableCarousel();
        }
    );
}
